import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Typography,
  Box,
  Chip,
  Divider,
  Skeleton,
  useTheme,
  alpha,
} from '@mui/material';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import FaxIcon from '@mui/icons-material/Fax';
import LanguageRoundedIcon from '@mui/icons-material/LanguageRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';

const HEAD_CELLS = [
  { label: 'รหัส / ประเภท', width: 140 },
  { label: 'ชื่อ / ผู้ติดต่อ', width: 200 },
  { label: 'ที่อยู่', width: 220 },
  { label: 'ภาษี / สาขา', width: 160 },
  { label: 'ช่องทางติดต่อ', width: 180 },
  { label: 'เครดิต', width: 80, align: 'center' },
];

function SkeletonRows({ count, colCount }) {
  return Array.from({ length: count }).map((_, idx) => (
    <TableRow key={`sk-${idx}`}>
      {Array.from({ length: colCount }).map((__, ci) => (
        <TableCell key={ci}>
          <Skeleton variant="text" width="80%" sx={{ fontSize: '0.875rem' }} />
          <Skeleton variant="text" width="55%" sx={{ fontSize: '0.75rem' }} />
        </TableCell>
      ))}
    </TableRow>
  ));
}

function ContactRow({ row, actionCell }) {
  const theme = useTheme();
  const address = [row.address1, row.address2, row.address3, row.tambon, row.amphoe, row.province, row.business_location].filter(Boolean).join(' ');

  return (
    <TableRow sx={{ '&:hover': { bgcolor: alpha(theme.palette.action.hover, 0.06) }, verticalAlign: 'top' }}>

      {/* รหัส / ประเภท */}
      <TableCell>
        <Typography variant="body2" fontWeight={600} sx={{ color: theme.palette.primary.main, mb: 0.3 }}>
          {row.contact_id || '-'}
        </Typography>
        {row.contact_type && (
          <Typography variant="caption" color="text.secondary" display="block">{row.contact_type}</Typography>
        )}
      </TableCell>

      {/* ชื่อ / ผู้ติดต่อ */}
      <TableCell>
        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.3 }}>{row.name || '-'}</Typography>
        {row.contact_name && (
          <Typography variant="caption" color="text.secondary">{row.contact_name}</Typography>
        )}
      </TableCell>

      {/* ที่อยู่ */}
      <TableCell>
        {address ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.6 }}>
            {address}
          </Typography>
        ) : (
          <Typography variant="caption" color="text.disabled">-</Typography>
        )}
        {row.zipcode && (
          <Chip label={row.zipcode} size="small" variant="outlined" sx={{ mt: 0.5, height: 18, fontSize: '0.65rem' }} />
        )}
      </TableCell>

      {/* ภาษี / สาขา */}
      <TableCell>
        <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 0.3 }}>
          {row.taxId || '-'}
        </Typography>
        {(row.branch_code || row.office_branch) && (
          <Typography variant="caption" color="text.secondary" display="block">
            {[row.branch_code, row.office_branch].filter(Boolean).join(' · ')}
          </Typography>
        )}
      </TableCell>

      {/* ช่องทางติดต่อ */}
      <TableCell>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
          {row.mobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PhoneAndroidIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
              <Typography variant="caption">{row.mobile}</Typography>
            </Box>
          )}
          {row.office_phone && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PhoneIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
              <Typography variant="caption">{row.office_phone}</Typography>
            </Box>
          )}
          {row.fax && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <FaxIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
              <Typography variant="caption">{row.fax}</Typography>
            </Box>
          )}
          {row.email && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <EmailIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
              <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>{row.email}</Typography>
            </Box>
          )}
          {row.website && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <LanguageRoundedIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
              <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>{row.website}</Typography>
            </Box>
          )}
          {row.line_id && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
              <Typography variant="caption">{row.line_id}</Typography>
            </Box>
          )}
          {!row.mobile && !row.office_phone && !row.email && !row.fax && !row.website && !row.line_id && (
            <Typography variant="caption" color="text.disabled">-</Typography>
          )}
        </Box>
      </TableCell>

      {/* เครดิต */}
      <TableCell align="center">
        {row.credit_days ? (
          <Chip
            label={`${row.credit_days} วัน`}
            size="small"
            color="info"
            variant="outlined"
            sx={{ fontWeight: 600, fontSize: '0.7rem' }}
          />
        ) : (
          <Typography variant="caption" color="text.disabled">-</Typography>
        )}
      </TableCell>

      {actionCell && <TableCell>{actionCell(row)}</TableCell>}
    </TableRow>
  );
}

function CustomContactTable({
  rows = [],
  page,
  size,
  total,
  onPageChange,
  onRowsPerPageChange,
  actionLabel,
  actionCell,
  isLoading = false,
}) {
  const theme = useTheme();
  const headCells = actionCell ? [...HEAD_CELLS, { label: actionLabel || 'จัดการ', width: 80, align: 'center' }] : HEAD_CELLS;

  return (
    <Paper elevation={0} sx={{ mb: 3, borderRadius: 2, overflow: 'hidden', border: `1px solid ${alpha(theme.palette.divider, 0.12)}` }}>
      <TableContainer>
        <Table sx={{ minWidth: 800 }}>
          <TableHead>
            <TableRow className="bg-theme-500 text-white">
              {headCells.map((col) => (
                <TableCell
                  key={col.label}
                  align={col.align || 'left'}
                  sx={{ fontWeight: 600, color: 'white', whiteSpace: 'nowrap', width: col.width, py: 1.5 }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <SkeletonRows count={size || 5} colCount={headCells.length} />
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={headCells.length} sx={{ py: 8 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" color="text.secondary">ไม่พบข้อมูล</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, idx) => (
                <ContactRow key={row._id || idx} row={row} actionCell={actionCell} />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Divider />
      <TablePagination
        component="div"
        onRowsPerPageChange={onRowsPerPageChange}
        page={page - 1}
        count={total || 0}
        rowsPerPage={size}
        onPageChange={(_, newPage) => onPageChange(newPage + 1)}
        labelRowsPerPage="แถวต่อหน้า:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} จาก ${count}`}
        sx={{
          '& .MuiTablePagination-toolbar': { height: 56 },
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { margin: 0 },
        }}
      />
    </Paper>
  );
}

export default CustomContactTable;
